/**
 * Foreground local wake and buffered Live-audio bridge.
 *
 * Its RMS detector is intentionally only an energy gate. Background noise can
 * trigger it; it does not claim semantic speech recognition. All samples stay
 * in process memory and are cleared by stop/rearm or worklet failure.
 */
export interface LiveWakeCallbacks {
  onWake(): void;
  onError(message: string): void;
  onLevel?(level: number): void;
}

export interface LiveWakeAdapters {
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  context(): AudioContext;
  node(context: AudioContext): AudioWorkletNode;
}

export const liveWakeAdapters: LiveWakeAdapters = {
  getUserMedia: constraints => navigator.mediaDevices.getUserMedia(constraints),
  context: () => new AudioContext({ sampleRate: 24000 }),
  node: context => new AudioWorkletNode(context, 'cm-live-wake'),
};

const STARTUP_TIMEOUT_MS = 15_000;

export class LiveWake {
  private generation = 0;
  private raw?: MediaStream;
  private context?: AudioContext;
  private source?: MediaStreamAudioSourceNode;
  private node?: AudioWorkletNode;
  private destination?: MediaStreamAudioDestinationNode;
  private startupTimer?: ReturnType<typeof setTimeout>;
  private woken = false;
  private failed = false;

  constructor(
    private callbacks: LiveWakeCallbacks,
    private adapters: LiveWakeAdapters = liveWakeAdapters,
  ) {}

  /** Create and resume audio resources in the caller's user-gesture turn. */
  async start(): Promise<boolean> {
    this.stop();
    const generation = this.generation;
    let stream: MediaStream | undefined;
    try {
      const context = this.adapters.context();
      this.context = context;
      const resumed = context.resume();
      // Stop can close this context while a permission prompt is pending.
      // Attach a rejection handler before the first await to avoid an
      // unhandled resume rejection in that legitimate cancellation path.
      void resumed.catch(() => {});
      // Permission must remain user initiated. Create/resume happens before
      // the first await so browsers retain the gesture association.
      stream = await this.adapters.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
        video: false,
      });
      if (generation !== this.generation) {
        stream.getTracks().forEach(track => track.stop());
        return false;
      }
      this.raw = stream;
      await resumed;
      if (generation !== this.generation) return false;
      await context.audioWorklet.addModule('/live-wake-worklet.js');
      if (generation !== this.generation) return false;
      if (context.sampleRate !== 24000) throw new Error('unsupported_sample_rate');
      const source = context.createMediaStreamSource(stream);
      const node = this.adapters.node(context);
      const destination = context.createMediaStreamDestination();
      this.source = source;
      this.node = node;
      this.destination = destination;
      node.port.onmessage = event => this.handleWorkletMessage(generation, event.data);
      // Destination only: the bridge never connects microphone input to local
      // speakers. The returned clone is the only stream the Live owner gets.
      source.connect(node);
      node.connect(destination);
      return true;
    } catch {
      if (generation !== this.generation) return false;
      this.fail('Local voice detection could not start.');
      return false;
    }
  }

  /** A clone prevents a VoiceSession from stopping the detector's raw stream. */
  stream(): MediaStream {
    if (!this.destination) throw new Error('Local voice detection is not running.');
    return this.destination.stream.clone();
  }

  /** Release only the worklet's bounded chronological startup buffer. */
  ready(): void {
    if (!this.node || !this.woken || this.failed) return;
    this.clearStartupTimer();
    this.node.port.postMessage({ type: 'ready' });
  }

  /** Return to the local gate after a receipt-backed authoritative idle close. */
  rearm(): void {
    if (!this.node || this.failed) return;
    this.clearStartupTimer();
    this.woken = false;
    this.node.port.postMessage({ type: 'rearm' });
  }

  /** Immediately stop the actual microphone/context and discard all PCM. */
  stop(): void {
    this.generation += 1;
    this.clearStartupTimer();
    this.woken = false;
    this.failed = false;
    // Clear references first, then release every resource independently. A
    // browser implementation can throw during a disconnect/postMessage; that
    // must never leave the real microphone or processed destination alive.
    const node = this.node;
    const source = this.source;
    const destination = this.destination;
    const raw = this.raw;
    const context = this.context;
    this.raw = undefined;
    this.context = undefined;
    this.source = undefined;
    this.node = undefined;
    this.destination = undefined;
    try { if (node) node.port.onmessage = null; } catch {}
    try { node?.port.postMessage({ type: 'stop' }); } catch {}
    try { node?.disconnect(); } catch {}
    try { source?.disconnect(); } catch {}
    try { destination?.disconnect(); } catch {}
    for (const track of raw?.getTracks() ?? []) {
      try { track.stop(); } catch {}
    }
    for (const track of destination?.stream.getTracks() ?? []) {
      try { track.stop(); } catch {}
    }
    try { void context?.close().catch(() => {}); } catch {}
  }

  private handleWorkletMessage(generation: number, message: unknown): void {
    if (generation !== this.generation || !message || typeof message !== 'object') return;
    const type = (message as { type?: unknown }).type;
    if (type === 'wake' && !this.woken && !this.failed) {
      this.woken = true;
      this.startupTimer = setTimeout(() => {
        if (generation === this.generation && this.woken) {
          this.fail('Live voice startup timed out.');
        }
      }, STARTUP_TIMEOUT_MS);
      try {
        this.callbacks.onWake();
      } catch {
        this.fail('Live voice startup could not continue.');
      }
      return;
    }
    if (type === 'error' && !this.failed) {
      this.fail('Live voice startup buffer reached its limit.');
      return;
    }
    if (type === 'level' && typeof (message as { level?: unknown }).level === 'number') {
      this.callbacks.onLevel?.(Math.max(0, Math.min(1, (message as { level: number }).level)));
    }
  }

  private clearStartupTimer(): void {
    if (this.startupTimer) clearTimeout(this.startupTimer);
    this.startupTimer = undefined;
  }

  private fail(message: string): void {
    if (this.failed) return;
    this.failed = true;
    // Resources must already be stopped if a consumer's error callback throws
    // or starts another UI transition. The finally preserves that invariant
    // for an injected/browser callback which itself raises.
    try {
      this.stop();
      this.callbacks.onError(message);
    } finally {
      this.stop();
    }
  }
}
